import {
    CYCLE_LAYOUT,
    Card,
    Checkbox,
    Colors,
    Icon,
    SelectOutlined,
    Text,
    type IconName,
} from '@amelio/ui-library';

import type { OptionProps } from '@amelio/ui-library';
import type { ReactNode } from 'react';

import { FormField } from '@/components/FormField/FormField';
import { useAmelioTranslations } from '@/hooks';
import { CYCLE_FIELD_IDLE_BORDER } from '@/pages/CycleEvaluation/components/cycleFieldChrome';

const TEMPLATE_LABEL_OVERLINE = [
    '[&>div>label]:uppercase [&>div>label]:font-bold!',
    '[&>div>label]:text-[var(--color-step-rail-muted)]!',
    '[&>div>label]:text-[11px]!',
    '[&>div>label]:leading-[16px]!',
    '[&>div>label]:tracking-[1.32px]!',
].join(' ');

const SUB_OPTIONS_SEPARATOR = 'mt-4 border-t border-[var(--color-step-rail-track)] pt-4';

const TILE_TITLE_TYPE = ['text-[18px]', 'leading-[28px]', 'tracking-tight'].join(' ');

const TILE_DESCRIPTION_TYPE = ['text-[14px]', 'leading-[22.75px]'].join(' ');

const SELECTION_CHECKBOX_BASE = 'rounded-full!';
const SELECTION_CHECKBOX_CHECKED =
    'border-[var(--color-step-rail-violet)]! bg-[var(--color-step-rail-violet)]!';
const SELECTION_CHECKBOX_IDLE = 'border-[var(--color-step-rail-track)]!';

const TILE_GLYPH_SIZE = 28;

export interface EvaluationTypeTileProps {
    iconName: IconName;
    label: string;
    description: string;
    isSelected: boolean;
    isReadOnly?: boolean;
    templateOptions: OptionProps[];
    templateId?: string;
    templateHint?: string;
    noTemplateMessage?: ReactNode;
    templateErrorMessage?: string;
    dataTestId: string;
    onToggle: () => void;
    onTemplateChange: (templateId: string) => void;
    children?: ReactNode;
}

export const EvaluationTypeTile = ({
    iconName,
    label,
    description,
    isSelected,
    isReadOnly = false,
    templateOptions,
    templateId,
    templateHint,
    noTemplateMessage,
    templateErrorMessage,
    dataTestId,
    onToggle,
    onTemplateChange,
    children,
}: EvaluationTypeTileProps) => {
    const { t } = useAmelioTranslations();

    const hasTemplates = templateOptions.length > 0;
    const hasTemplateError = Boolean(templateErrorMessage);

    return (
        <Card
            bgColor={'bg-white'}
            borderColor={isSelected ? Colors.STEP_RAIL_VIOLET : Colors.BASE_50}
            padding={'lg'}
            borderRadius={'xl'}
        >
            <div
                className={CYCLE_LAYOUT.tileHeaderLayout}
                data-testid={dataTestId}
            >
                <div className={CYCLE_LAYOUT.tileIdentityContent}>
                    <Checkbox
                        checked={isSelected}
                        readOnly={isReadOnly}
                        size={'lg'}
                        className={`${SELECTION_CHECKBOX_BASE} ${isSelected ? SELECTION_CHECKBOX_CHECKED : SELECTION_CHECKBOX_IDLE}`}
                        onChange={onToggle}
                        ariaLabel={label}
                        dataTestId={`${dataTestId}-checkbox`}
                    />

                    <Icon
                        name={iconName}
                        size={TILE_GLYPH_SIZE}
                        color={isSelected ? Colors.STEP_RAIL_VIOLET : Colors.BASE_400}
                        ariaHidden={true}
                    />

                    <div className={'min-w-0 flex-1'}>
                        <Text
                            weight={'bold'}
                            className={TILE_TITLE_TYPE}
                        >
                            {label}
                        </Text>

                        <Text
                            color={Colors.STEP_RAIL_MUTED}
                            weight={isSelected ? 'medium' : 'regular'}
                            className={`${TILE_DESCRIPTION_TYPE} mt-1`}
                        >
                            {description}
                        </Text>
                    </div>
                </div>

                {isSelected && (
                    <div className={CYCLE_LAYOUT.tileTemplateColumn}>
                        {hasTemplates ? (
                            <FormField
                                label={t('cycleEvaluationEvaluationElements.template.label')}
                                helperText={templateHint}
                                dataTestId={`${dataTestId}-template-field`}
                                errorMessage={templateErrorMessage}
                                className={TEMPLATE_LABEL_OVERLINE}
                            >
                                <SelectOutlined
                                    options={templateOptions}
                                    value={templateId}
                                    placeholder={t(
                                        'cycleEvaluationEvaluationElements.template.placeholder',
                                    )}
                                    error={hasTemplateError}
                                    searchable={true}
                                    readOnly={isReadOnly}
                                    sort={'none'}
                                    onChange={onTemplateChange}
                                    size={'mdPlus'}
                                    triggerBorderClassName={
                                        hasTemplateError
                                            ? undefined
                                            : `border ${CYCLE_FIELD_IDLE_BORDER}`
                                    }
                                    triggerHoverBorderClassName={'hover:border-primary'}
                                    dataTestId={`${dataTestId}-template-select`}
                                />
                            </FormField>
                        ) : (
                            noTemplateMessage
                        )}
                    </div>
                )}
            </div>

            {isSelected && children && (
                <div
                    className={SUB_OPTIONS_SEPARATOR}
                    data-testid={`${dataTestId}-sub-options`}
                >
                    {children}
                </div>
            )}
        </Card>
    );
};
